import { helpers, types } from '@shopuptech/ui-core';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import type { ItemsEntity } from '../mainAction.types';
import { checkLoginAction, checkPermissionsAction } from '../mainActions';
import { LOGIN_PATH, NO_PERMISSION_PATH } from '../modules/mainhome/constants';

const { getPermissionDict, initialisePermissionsHook } = helpers;

const EXCLUDED_PATHS = [
  LOGIN_PATH,
  NO_PERMISSION_PATH,
  '/dashboard',
  '/forgot-password',
];

const useRequireLogin = (isLoggedIn: boolean) => {
  const dispatch: types.Dispatch<any> = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [loggedIn, setLoggedIn] = useState(isLoggedIn);
  const [permissions, setPermission] = useState<types.permissionType>({});
  const [permissionList, setPermissionList] = useState<ItemsEntity[]>([]);

  const handlePermissions = useCallback(
    (items: ItemsEntity[]) => {
      if (Array.isArray(items) && items.length > 0) {
        setPermissionList(items);
        initialisePermissionsHook(items);
        const permissionsUpdated = getPermissionDict(items);
        setPermission(permissionsUpdated);

        const params = new URLSearchParams(location.search);
        const redirectPath = params.get('ref');

        if (
          redirectPath &&
          !EXCLUDED_PATHS.some((path) => redirectPath.startsWith(path))
        ) {
          window.location.href = redirectPath;
        } else if (location.pathname === LOGIN_PATH) {
          history.replace('/dashboard');
        }
      }
    },
    [location.pathname, location.search, history],
  );

  const handleError = useCallback(() => {
    const shouldStoreRef = !EXCLUDED_PATHS.some((path) =>
      location.pathname.startsWith(path),
    );
    const redirectUrl = shouldStoreRef
      ? `${LOGIN_PATH}?ref=${encodeURIComponent(location.pathname)}`
      : LOGIN_PATH;
    if (
      location.pathname !== LOGIN_PATH &&
      !location.pathname.startsWith('/forgot-password')
    ) {
      history.replace(redirectUrl);
    }
  }, [location.pathname, history]);

  useEffect(() => {
    const promises = [];
    promises.push(dispatch(checkLoginAction()));
    promises.push(dispatch(checkPermissionsAction()));
    Promise.all(promises)
      .then((responseArray) => {
        const [, permissionResponse] = responseArray;

        setLoggedIn(true);
        const { data: { items = [] } = {} } = permissionResponse || {};
        handlePermissions(items);
      })
      .catch(handleError);
  }, [isLoggedIn, handlePermissions, handleError, dispatch]);

  return {
    loggedIn,
    permissions,
    permissionList,
  };
};

export default useRequireLogin;
